import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import {
  apiAdminGetWinLossReport,
  apiAdminExportWinLossReport,
  ApiRequestError,
} from '@/lib/api'
import type { WinLossReport } from '@nexbit/types'
import { formatCurrency } from '@/lib/utils'
import { format, startOfMonth, endOfDay } from 'date-fns'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, ArrowLeft, Search, TrendingUp, ChevronRight, Download } from 'lucide-react'
import { cn } from '@/lib/utils'

const toDate = (d: Date) => format(d, 'yyyy-MM-dd')
const signed = (v: string) => (parseFloat(v) >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold')

const inputClass = cn(
  'h-9 rounded-[var(--radius-sm)] border border-input bg-transparent px-3',
  'font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
)

export default function AdminReportsMasterAgentsPage() {
  const { masterId } = useParams<{ masterId: string }>()
  const navigate = useNavigate()
  const [sp] = useSearchParams()
  const initFrom = sp.get('from')
  const initTo = sp.get('to')
  const masterCode = sp.get('masterCode') ?? ''
  const masterName = sp.get('masterName') ?? ''

  const [from, setFrom] = useState(initFrom ? format(new Date(initFrom), 'yyyy-MM-dd') : toDate(startOfMonth(new Date())))
  const [to, setTo] = useState(initTo ? format(new Date(initTo), 'yyyy-MM-dd') : toDate(endOfDay(new Date())))
  const [rows, setRows] = useState<WinLossReport[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [applied, setApplied] = useState<{ from: string; to: string } | null>(null)

  const search = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!masterId) return
    setLoading(true); setError(null)
    try {
      const range = { from: new Date(from).toISOString(), to: new Date(to + 'T23:59:59').toISOString() }
      setRows(await apiAdminGetWinLossReport({ ...range, masterId }))
      setApplied(range)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'โหลดรายงานไม่สำเร็จ')
    } finally { setLoading(false) }
  }, [masterId, from, to])

  useEffect(() => { if (initFrom && initTo && masterId) void search() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleExport() {
    if (!applied || !masterId) return
    setExporting(true); setError(null)
    try { await apiAdminExportWinLossReport({ ...applied, masterId }) }
    catch (err) { setError(err instanceof ApiRequestError ? err.message : 'ส่งออกไม่สำเร็จ') }
    finally { setExporting(false) }
  }

  function drilldown(r: WinLossReport) {
    if (!r.partnerId || !applied) return
    const q = new URLSearchParams({
      from: applied.from, to: applied.to,
      partnerCode: r.partnerCode, agentShareRate: r.agentShareRate,
    })
    navigate(`/admin/reports/agent/${r.partnerId}?${q.toString()}`)
  }

  const totals = rows?.reduce((a, r) => ({
    bet: a.bet + parseFloat(r.totalBetAmount),
    win: a.win + parseFloat(r.totalWinAmount),
    profit: a.profit + parseFloat(r.netProfit),
    agentNet: a.agentNet + parseFloat(r.agentNet),
    masterNet: a.masterNet + parseFloat(r.masterNet),
    adminNet: a.adminNet + parseFloat(r.adminNet),
  }), { bet: 0, win: 0, profit: 0, agentNet: 0, masterNet: 0, adminNet: 0 })

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-muted-foreground" asChild>
          <Link to="/admin/reports"><ArrowLeft className="h-3.5 w-3.5" />รายงานกำไร/ขาดทุน</Link>
        </Button>
        <span className="text-muted-foreground/40">·</span>
        <span className="text-sm font-semibold">
          {masterCode || 'มาสเตอร์'}{masterName ? ` — ${masterName}` : ''} · เอเจนต์ในสาย
        </span>
      </div>

      <form onSubmit={(e) => void search(e)}>
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] mb-4">
          <div className="px-4 py-2.5 bg-secondary">
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">กรอง</span>
          </div>
          <div className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">ตั้งแต่</Label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required className={inputClass} />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">ถึง</Label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} required className={inputClass} />
            </div>
            <Button type="submit" size="sm" className="h-9 text-xs gap-1.5" disabled={loading}>
              <Search className="h-3.5 w-3.5" />ค้นหา
            </Button>
          </div>
        </div>
      </form>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden">
          <div className="px-4 py-2.5 bg-secondary"><Skeleton className="h-3 w-24" /></div>
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => <div key={i} className="flex gap-4 px-4 py-3"><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-20" /></div>)}
          </div>
        </div>
      ) : rows !== null ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
          <div className="px-4 py-2.5 bg-secondary flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5 text-secondary-foreground/60" />
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">เอเจนต์</span>
            <span className="font-mono text-xs text-secondary-foreground/60">{rows.length} เอเจนต์</span>
            {rows.length > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={() => void handleExport()} disabled={exporting} className="ml-auto h-7 text-xs gap-1.5">
                <Download className="h-3.5 w-3.5" />{exporting ? 'กำลังส่งออก...' : 'ส่งออก CSV'}
              </Button>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="py-12 text-center"><p className="text-sm text-muted-foreground">ไม่พบเอเจนต์ที่มีการเดิมพันในช่วงนี้</p></div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>เอเจนต์</TableHead>
                    <TableHead className="text-right">หุ้นเอเจนต์</TableHead>
                    <TableHead className="text-right">หุ้นมาสเตอร์</TableHead>
                    <TableHead className="text-right">ยอดเดิมพัน</TableHead>
                    <TableHead className="text-right">ยอดจ่าย</TableHead>
                    <TableHead className="text-right">กำไรสุทธิ</TableHead>
                    <TableHead className="text-right">ส่วนแบ่งเอเจนต์</TableHead>
                    <TableHead className="text-right">ส่วนแบ่งมาสเตอร์</TableHead>
                    <TableHead className="text-right">ส่วนแบ่งแอดมิน</TableHead>
                    <TableHead className="text-right">สมาชิก</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.partnerId ?? r.partnerCode} className="cursor-pointer hover:bg-muted/50" onClick={() => drilldown(r)}>
                      <TableCell className="font-mono text-sm font-semibold">{r.partnerCode}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">{(parseFloat(r.agentShareRate) * 100).toFixed(0)}%</TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">{(parseFloat(r.masterShareRate) * 100).toFixed(0)}%</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(r.totalBetAmount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(r.totalWinAmount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm"><span className={signed(r.netProfit)}>{formatCurrency(r.netProfit)}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm"><span className={signed(r.agentNet)}>{formatCurrency(r.agentNet)}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm"><span className={signed(r.masterNet)}>{formatCurrency(r.masterNet)}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm"><span className={signed(r.adminNet)}>{formatCurrency(r.adminNet)}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">{r.memberCount.toLocaleString()}</TableCell>
                      <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totals && (
                <div className="px-4 py-3 border-t border-border bg-secondary/20 flex flex-wrap gap-x-6 gap-y-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">ยอดรวม</span>
                  <span className="font-mono text-xs">ยอดเดิมพัน: <strong>{formatCurrency(totals.bet.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">ยอดจ่าย: <strong>{formatCurrency(totals.win.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">กำไรสุทธิ: <strong className={totals.profit >= 0 ? 'text-success' : 'text-destructive'}>{formatCurrency(totals.profit.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">ส่วนแบ่งมาสเตอร์: <strong className={totals.masterNet >= 0 ? 'text-success' : 'text-destructive'}>{formatCurrency(totals.masterNet.toFixed(2))}</strong></span>
                  <span className="font-mono text-xs">ส่วนแบ่งแอดมิน: <strong className={totals.adminNet >= 0 ? 'text-success' : 'text-destructive'}>{formatCurrency(totals.adminNet.toFixed(2))}</strong></span>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-[var(--radius)] py-12 text-center">
          <p className="text-sm text-muted-foreground">เลือกช่วงวันที่แล้วกดค้นหา</p>
        </div>
      )}
    </div>
  )
}
